import { Button } from "@/components/ui/button";
import { DatePickerWithPresets } from "@/components/ui/date-picker-with-presets";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import { MentorSlot, useUpdateMatchingOverride } from "@/lib/api/user";
import { format, parseISO } from "date-fns";
import { Loader2, X } from "lucide-react";
import { useState } from "react";

interface AdjustOverrideDateDialogProps {
  mentorSlot: MentorSlot;
  isOpen: boolean;
  onClose: () => void;
}

export function AdjustOverrideDateDialog({
  mentorSlot,
  isOpen,
  onClose,
}: AdjustOverrideDateDialogProps) {
  const updateOverride = useUpdateMatchingOverride();
  const { toast } = useToast();
  const currentEndDate = mentorSlot.slot_config.override?.endDate;
  const [endDate, setEndDate] = useState<Date | undefined>(
    // parse only the date part so a UTC timestamp doesn't shift a day locally
    currentEndDate ? parseISO(currentEndDate.slice(0, 10)) : undefined,
  );

  const handleSubmit = async () => {
    if (!endDate) return;
    try {
      await updateOverride.mutateAsync({
        userId: mentorSlot.mentor.id,
        data: { end_date: format(endDate, "yyyy-MM-dd") },
      });
      toast({
        title: "End date updated",
        description: `Daily slots for ${mentorSlot.mentor.name} will stay off until ${format(
          endDate,
          "MMM d, yyyy",
        )}.`,
      });
      onClose();
    } catch (_error) {
      toast({
        title: "Error",
        description: "Failed to update override end date",
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="gap-0 p-0 sm:max-w-md sm:rounded-xl"
        hideCloseButton
      >
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-5">
          <DialogTitle className="text-xl font-bold tracking-tight">
            Adjust end date
          </DialogTitle>
          <DialogClose className="opacity-70 transition-opacity hover:opacity-100 focus:outline-none">
            <X className="h-5 w-5" />
            <span className="sr-only">Close</span>
          </DialogClose>
        </div>

        <div className="flex flex-col gap-5 p-6">
          <div className="flex flex-col gap-2">
            <Label className="text-sm font-normal">End date</Label>
            <DatePickerWithPresets
              date={endDate}
              onDateChange={setEndDate}
              className="h-11 w-full rounded-lg px-3"
              showCalendarIcon
              modal
            />
          </div>

          <div className="flex flex-col gap-2">
            <p className="text-xs text-gray-400">
              The pause override for {mentorSlot.mentor.name} will keep running
              until the selected date
            </p>
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="h-10 rounded-lg"
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button
                className="h-10 rounded-lg"
                onClick={handleSubmit}
                disabled={updateOverride.isPending || !endDate}
              >
                {updateOverride.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Save End Date
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
